import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  type DataTableToolbarProps,
  DataTableViewOptions,
  DateUI,
  FacetedFilterStandalone,
  type Palettes,
  PrimaryFullScreenSpinner,
  StatusBase,
  Txt,
  toast,
  type UntitledUIIconType,
} from "@repo/ui";
import { neverFunc } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { ClockStopwatch, Loading01, SlashCircle01, X } from "@untitledui/icons";
import { Suspense, useMemo, useState } from "react";
import { PiHorse } from "react-icons/pi";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { OpsPlatformJobFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const HUNG_THRESHOLD_MS = 10 * 60 * 1000;

export const Route = createFileRoute("/__auth/jobs")({
  head: () => opsHead("Jobs"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const jobStatuses = [
  "scheduled",
  "running",
  "hung",
  "retrying",
  "failed",
] as const;
type JobStatus = (typeof jobStatuses)[number];

type Job = OpsPlatformJobFragment & {
  status: JobStatus;
  locked: boolean;
};

const jobStatusDisplay: Record<
  JobStatus,
  { label: string; color: Palettes; icon: UntitledUIIconType }
> = {
  scheduled: { label: "Scheduled", color: "brand", icon: ClockStopwatch },
  running: { label: "Running", color: "success", icon: Loading01 },
  hung: {
    label: "Hung",
    color: "warning",
    icon: PiHorse as UntitledUIIconType,
  },
  retrying: { label: "Retrying", color: "warning", icon: ClockStopwatch },
  failed: { label: "Failed", color: "error", icon: SlashCircle01 },
};

const calculateJobStatusForUI = ({
  attempts,
  maxAttempts,
  lockedAt,
}: OpsPlatformJobFragment): JobStatus => {
  if (lockedAt !== null && lockedAt !== undefined) {
    const isHung = Date.now() - lockedAt.getTime() > HUNG_THRESHOLD_MS;
    return isHung ? "hung" : "running";
  }
  if (attempts < 1) {
    return "scheduled";
  }
  if (attempts >= maxAttempts) {
    return "failed";
  }
  return "retrying";
};

function View() {
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  const jobsQuery = useSuspenseQuery(
    graphql(`
      query AdminPlatformJobs {
        platformJobs {
          ...OpsPlatformJob
        }
      }
      fragment OpsPlatformJob on PlatformJob {
        id
        key
        taskIdentifier
        queueName
        queueLockedAt
        queueLockedBy
        createdAt
        attempts
        maxAttempts
        lastError
        lockedAt
        lockedBy
      }
    `),
  );

  const [resetPlatformJobAttempts, { loading: resetLoading }] = useMutation(
    graphql(`
      mutation AdminResetPlatformJobAttempts($input: ResetPlatformJobAttemptsInput!) {
        resetPlatformJobAttempts(input: $input) {
          success
        }
      }
    `),
  );
  const [permafailPlatformJob, { loading: permafailLoading }] = useMutation(
    graphql(`
      mutation AdminPermafailPlatformJob($input: PermafailPlatformJobInput!) {
        permafailPlatformJob(input: $input) {
          success
        }
      }
    `),
  );
  const [cleanupPlatformJobQueue, { loading: cleanupLoading }] = useMutation(
    graphql(`
      mutation AdminCleanupPlatformJobQueue {
        cleanupPlatformJobQueue {
          success
        }
      }
    `),
  );
  const [unlockPlatformJob, { loading: unlockLoading }] = useMutation(
    graphql(`
      mutation AdminUnlockPlatformJob($input: UnlockPlatformJobInput!) {
        unlockPlatformJob(input: $input) {
          success
        }
      }
    `),
  );

  const jobListItems = (jobsQuery.data.platformJobs ?? []).map((j): Job => {
    return {
      ...j,
      status: calculateJobStatusForUI(j),
      locked: !!j.lockedAt,
    };
  });
  const mutationLoading = resetLoading || permafailLoading || unlockLoading;

  const runJobAction = async ({
    jobId,
    actionKind,
  }: {
    jobId: string;
    actionKind: "job.reset_attempts" | "job.permafail" | "job.unlock";
  }) => {
    try {
      switch (actionKind) {
        case "job.reset_attempts":
          await resetPlatformJobAttempts({
            variables: {
              input: {
                jobId,
              },
            },
          });
          break;
        case "job.permafail":
          await permafailPlatformJob({
            variables: {
              input: {
                jobId,
              },
            },
          });
          break;
        case "job.unlock":
          await unlockPlatformJob({
            variables: {
              input: {
                jobId,
              },
            },
          });
          break;
        default:
          neverFunc(actionKind);
      }

      await jobsQuery.refetch();
      toast({
        kind: "success",
        title: "Job updated",
        autoHide: true,
      });
    } catch (error) {
      toast({
        kind: "error",
        autoHide: false,
        title: "Failed to update job",
        description: String(error),
      });
    }
  };

  const handleCleanup = async () => {
    try {
      await cleanupPlatformJobQueue();
      await jobsQuery.refetch();
      toast({
        kind: "success",
        title: "Job queue cleanup completed",
        autoHide: true,
      });
    } catch (error) {
      toast({
        kind: "error",
        autoHide: false,
        title: "Failed to cleanup job queue",
        description: String(error),
      });
    }
  };

  return (
    <AdminPageContainer testId="admin-platform-jobs">
      <AdminPageHeader
        title="Jobs"
        buttons={[
          <Button
            key="job-queue-cleanup"
            disabled={cleanupLoading}
            onClick={() => {
              void handleCleanup();
            }}
          >
            Cleanup
          </Button>,
        ]}
      />
      <DataTable
        data={jobListItems}
        toolbarComponent={DataTableToolbar}
        activeRowId={expandedJobId ?? undefined}
        onRowClick={(row) => {
          const active = row.original.id === expandedJobId;
          setExpandedJobId(active ? null : row.original.id);
        }}
        columnSortProps={{
          id: "jobId",
          desc: true,
        }}
        isRowExpanded={(row) => row.original.id === expandedJobId}
        renderExpandedRow={(row) => {
          const job = row.original;
          return (
            <div className="rounded-md p-4">
              <h3 className="mb-3 font-semibold text-sm">Job #{job.id}</h3>
              <DetailRow label="Job Key" value={<Txt>{job.key}</Txt>} />
              <DetailRow
                label="Queue Name"
                value={<Txt>{job.queueName ?? "No queue"}</Txt>}
              />
              {job.queueLockedAt && (
                <DetailRow
                  label="Queue locked at"
                  value={
                    <Txt>
                      <DateUI date={job.queueLockedAt} variant="DATETIME_TZ" />
                    </Txt>
                  }
                />
              )}
              {job.queueLockedBy && (
                <DetailRow
                  label="Queue locked by"
                  value={<Txt>{job.queueLockedBy}</Txt>}
                />
              )}
              <DetailRow
                label="Attempts"
                value={`${job.attempts} of ${job.maxAttempts}${
                  job.attempts === job.maxAttempts ? " (max. reached)" : ""
                }`}
              />
              <DetailRow className="border-b-transparent" label="Last Error">
                <pre>{job.lastError}</pre>
              </DetailRow>
              <div className="mt-3 flex flex-row justify-end gap-2">
                <Button
                  variant="primary"
                  disabled={!job.lockedBy && !job.queueLockedBy}
                  onClick={(event) => {
                    event.stopPropagation();
                    void runJobAction({
                      actionKind: "job.unlock",
                      jobId: job.id,
                    });
                  }}
                >
                  {job.lockedBy && job.lockedBy === job.queueLockedBy
                    ? "Unlock job and queue"
                    : "Unlock job"}
                </Button>
                <Button
                  variant="primary"
                  disabled={job.attempts !== job.maxAttempts || mutationLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    void runJobAction({
                      actionKind: "job.reset_attempts",
                      jobId: job.id,
                    });
                  }}
                >
                  Reset Attempts
                </Button>
                <Button
                  variant="destructive"
                  disabled={job.attempts === job.maxAttempts || mutationLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    void runJobAction({
                      actionKind: "job.permafail",
                      jobId: job.id,
                    });
                  }}
                >
                  Permafail
                </Button>
              </div>
            </div>
          );
        }}
        columns={[
          {
            id: "jobId",
            accessorFn: (row) => row.id,
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Job ID" />
            ),
            cell: ({ row }) => <Txt variant="mono">{row.original.id}</Txt>,
          },
          {
            id: "taskIdentifier",
            accessorFn: (row) => row.taskIdentifier,
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Task ID" />
            ),
            cell: ({ row }) => <Txt>{row.original.taskIdentifier}</Txt>,
            filterFn: (row, _id, value) =>
              value.includes(row.original.taskIdentifier),
          },
          {
            id: "createdAt",
            accessorFn: (row) => row.createdAt.toISOString(),
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Created At" />
            ),
            cell: ({ row }) => (
              <Txt>
                <DateUI date={row.original.createdAt} variant="DATETIME_TZ" />
              </Txt>
            ),
          },
          {
            id: "status",
            accessorFn: (row) => row.status,
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Status" />
            ),
            cell: ({ row }) => {
              const { icon, color, label } =
                jobStatusDisplay[row.original.status];
              return <StatusBase {...{ icon, color, label }} />;
            },
            filterFn: (row, _id, value) => value.includes(row.original.status),
          },
          {
            id: "locked",
            accessorFn: (row) => (row.locked ? "locked" : "not_locked"),
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Locked?" />
            ),
            filterFn: (row, _id, value) =>
              value.includes(row.original.locked ? "locked" : "not_locked"),
            cell: ({ row }) => {
              if (!row.original.lockedAt) {
                return <div>Not locked</div>;
              }
              if (row.original.lockedBy) {
                return (
                  <div className="flex flex-col gap-1">
                    <Txt>{row.original.lockedBy}</Txt>
                    <Txt>
                      <DateUI
                        date={row.original.lockedAt}
                        variant="DATETIME_TZ"
                      />
                    </Txt>
                  </div>
                );
              }
              return (
                <Txt>
                  <DateUI date={row.original.lockedAt} variant="DATETIME_TZ" />
                </Txt>
              );
            },
          },
        ]}
        className="overflow-hidden border"
      />
    </AdminPageContainer>
  );
}

function DataTableToolbar({ table }: DataTableToolbarProps<Job>) {
  const preFilteredRows = table.getPreFilteredRowModel().rows;

  const taskIdentifierOptions = useMemo(
    () =>
      Array.from(
        new Set(preFilteredRows.map((r) => r.original.taskIdentifier)),
      ).map((value) => ({ label: value, value })),
    [preFilteredRows],
  );

  const isFiltered =
    table.getPreFilteredRowModel().rows.length >
    table.getFilteredRowModel().rows.length;

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 flex-wrap items-center gap-1">
        {table.getColumn("status") && (
          <FacetedFilterStandalone
            column={table.getColumn("status")}
            title="Status"
            options={jobStatuses.map((value) => ({
              label: jobStatusDisplay[value].label,
              value,
            }))}
          />
        )}
        {table.getColumn("taskIdentifier") && (
          <FacetedFilterStandalone
            column={table.getColumn("taskIdentifier")}
            title="Task ID"
            options={taskIdentifierOptions}
            contentClassName="w-[450px]"
          />
        )}
        {table.getColumn("locked") && (
          <FacetedFilterStandalone
            column={table.getColumn("locked")}
            title="Locked"
            options={[
              { label: "Locked", value: "locked" },
              { label: "Not locked", value: "not_locked" },
            ]}
          />
        )}
        {isFiltered && (
          <Button
            rightIcon={<X />}
            variant="ghost"
            size="sm"
            onClick={() => table.resetColumnFilters()}
          >
            Reset
          </Button>
        )}
      </div>
      <DataTableViewOptions table={table} />
    </div>
  );
}
